/**
 * Integrations.
 *
 * Credentials are write-only: this screen can send them but never reads one
 * back, and the API returns only connection status.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/api/client';
import { integrationApi, siteApi } from '@/api/endpoints';
import { useAuth } from '@/app/auth';
import { Permission } from '@/app/permissions';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  Field,
  LoadingState,
  PageHeader,
} from '@/components/ui';
import {
  formatRelative,
  inputClass,
} from '@/utils/format';
import type { IntegrationSummary } from '@/types/api';

export function IntegrationsPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [selectedSite, setSelectedSite] = useState('');

  const sitesQuery = useQuery({ queryKey: ['sites'], queryFn: () => siteApi.list({ page_size: 100 }) });
  const integrationsQuery = useQuery({
    queryKey: ['integrations'],
    queryFn: () => integrationApi.list(),
  });
  const googleStatusQuery = useQuery({
    queryKey: ['google-status'],
    queryFn: () => integrationApi.googleStatus(),
  });

  useEffect(() => {
    if (!selectedSite && sitesQuery.data?.items.length) {
      setSelectedSite(sitesQuery.data.items[0].id);
    }
  }, [sitesQuery.data, selectedSite]);

  // The OAuth callback returns here with a result flag.
  const googleConnected = params.get('google_connected');
  const googleError = params.get('google_error');
  useEffect(() => {
    if (googleConnected || googleError) {
      void queryClient.invalidateQueries({ queryKey: ['integrations'] });
    }
  }, [googleConnected, googleError, queryClient]);

  const canManage = can(Permission.INTEGRATION_MANAGE);
  const integrations = integrationsQuery.data ?? [];

  return (
    <div className="integrations-workspace">
      <PageHeader
        title="Integrations"
        description="Connect your search data and the system where your website lives."
      />

      {googleConnected ? (
        <div className="mb-5">
          <Callout tone="success" title="Google connected">
            Choose which Search Console property corresponds to this website below.
          </Callout>
        </div>
      ) : null}
      {googleError ? (
        <div className="mb-5">
          <Callout tone="danger" title="Google connection failed">
            {googleError.replace(/_/g, ' ')}.{' '}
            <button
              type="button"
              className="font-medium text-brand hover:underline"
              onClick={() => setParams(new URLSearchParams(), { replace: true })}
            >
              Dismiss
            </button>
          </Callout>
        </div>
      ) : null}

      {sitesQuery.data && sitesQuery.data.items.length > 1 ? (
        <Card className="mb-5">
          <Field label="Website" htmlFor="site-select" hint="Integrations are per website.">
            <select
              id="site-select"
              value={selectedSite}
              onChange={(event) => setSelectedSite(event.target.value)}
              className={inputClass}
            >
              {sitesQuery.data.items.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
          </Field>
        </Card>
      ) : null}

      {integrationsQuery.isLoading ? <LoadingState /> : null}

      <div className="integrations-provider-grid">
        {selectedSite && <Card><CardHeader title="New to Google Search Console?" description="Answer a few questions to connect an existing property or create and verify a new one." /><Link className="m-button" to={`/sites/${selectedSite}/search-setup`}>Open guided Google setup</Link></Card>}
        <GoogleCard
          configured={googleStatusQuery.data?.configured ?? false}
          redirectUri={googleStatusQuery.data?.redirect_uri ?? ''}
          integration={findIntegration(integrations, selectedSite, 'google_search_console')}
          siteId={selectedSite}
          canManage={canManage}
        />

        <WordPressCard
          integration={findIntegration(integrations, selectedSite, 'wordpress')}
          siteId={selectedSite}
          canManage={canManage}
        />

        <GitCard
          integration={findIntegration(integrations, selectedSite, 'github')}
          siteId={selectedSite}
          canManage={canManage}
        />
      </div>
    </div>
  );
}

function findIntegration(
  integrations: IntegrationSummary[],
  siteId: string,
  provider: string,
): IntegrationSummary | undefined {
  return integrations.find(
    (integration) => integration.site_id === siteId && integration.provider === provider,
  );
}

function StatusRow({ integration }: { integration?: IntegrationSummary }) {
  if (!integration) return <Badge tone="neutral">Not connected</Badge>;
  const tone =
    integration.status === 'connected'
      ? 'success'
      : integration.status === 'error' || integration.status === 'expired'
        ? 'danger'
        : 'warning';
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge tone={tone}>{integration.status}</Badge>
      {integration.external_account_email ? (
        <span className="text-xs text-ink-subtle">{integration.external_account_email}</span>
      ) : null}
      {integration.last_sync_at ? (
        <span className="text-xs text-ink-subtle">
          synced {formatRelative(integration.last_sync_at)}
        </span>
      ) : null}
    </div>
  );
}

// -------------------------------------------------------------------- google
function GoogleCard({
  configured,
  redirectUri,
  integration,
  siteId,
  canManage,
}: {
  configured: boolean;
  redirectUri: string;
  integration?: IntegrationSummary;
  siteId: string;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();

  const connectMutation = useMutation({
    mutationFn: () =>
      integrationApi.connectGoogle({
        site_id: siteId,
        redirect_after: `${window.location.origin}/integrations`,
      }),
    onSuccess: (result) => {
      window.location.href = result.authorization_url;
    },
  });

  const propertiesQuery = useQuery({
    queryKey: ['google-properties', integration?.id],
    queryFn: () => integrationApi.googleProperties(integration!.id),
    enabled: Boolean(integration && integration.status !== 'revoked'),
    retry: false,
  });

  const selectMutation = useMutation({
    mutationFn: (propertyUrl: string) =>
      integrationApi.selectGoogleProperty(integration!.id, { property_url: propertyUrl }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['integrations'] }),
  });

  const syncMutation = useMutation({
    mutationFn: () => integrationApi.syncGoogle(integration!.id),
  });

  const disconnectMutation = useMutation({
    mutationFn: () => integrationApi.disconnect(integration!.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['integrations'] }),
  });

  const selectedProperty = integration?.config?.property_url as string | undefined;

  return (
    <Card>
      <CardHeader
        title="Google Search Console"
        description="Read-only access to your search performance. This is what unlocks click-through, striking-distance and content-decay analysis."
        action={<StatusRow integration={integration} />}
      />

      {!configured ? (
        <Callout tone="warning" title="Not configured on this deployment">
          <p>
            An administrator needs to set <code className="font-mono">GOOGLE_CLIENT_ID</code> and{' '}
            <code className="font-mono">GOOGLE_CLIENT_SECRET</code>, and register this redirect URI
            in Google Cloud Console:
          </p>
          <code className="mt-1 block font-mono text-xs break-all">{redirectUri}</code>
        </Callout>
      ) : !integration || integration.status === 'revoked' ? (
        <div>
          <Button
            variant="primary"
            disabled={!canManage || !siteId}
            loading={connectMutation.isPending}
            onClick={() => connectMutation.mutate()}
          >
            Connect Google Search Console
          </Button>
          <p className="mt-2 text-xs text-ink-subtle">
            We request read-only scope. SEO RankFix never modifies anything in your Google
            account.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {selectedProperty ? (
            <p className="text-sm text-ink-muted">
              Property: <span className="font-mono text-ink">{selectedProperty}</span>
            </p>
          ) : null}

          {propertiesQuery.data ? (
            <div>
              <label htmlFor="gsc-property" className="block text-sm font-medium text-ink">
                Search Console property
              </label>
              <select
                id="gsc-property"
                defaultValue={selectedProperty ?? ''}
                onChange={(event) => event.target.value && selectMutation.mutate(event.target.value)}
                className={`${inputClass} mt-1.5`}
                disabled={!canManage || selectMutation.isPending}
              >
                <option value="">Select a property…</option>
                {propertiesQuery.data.map((property) => (
                  <option
                    key={property.property_url}
                    value={property.property_url}
                    disabled={!property.readable}
                  >
                    {property.property_url}
                    {property.readable ? '' : ' (no read access)'}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {propertiesQuery.isError ? (
            <Callout tone="danger">
              {propertiesQuery.error instanceof ApiError
                ? propertiesQuery.error.message
                : 'Could not list your Search Console properties.'}
            </Callout>
          ) : null}

          {integration.last_error ? (
            <Callout tone="danger">{integration.last_error}</Callout>
          ) : null}

          <div className="flex flex-wrap gap-2">
            {selectedProperty ? (
              <Button
                size="sm"
                loading={syncMutation.isPending}
                disabled={!canManage}
                onClick={() => syncMutation.mutate()}
              >
                Sync now
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="ghost"
              disabled={!canManage}
              loading={disconnectMutation.isPending}
              onClick={() => disconnectMutation.mutate()}
            >
              Disconnect
            </Button>
          </div>
          {syncMutation.isSuccess ? (
            <p className="text-xs text-success">Sync queued. Data appears within a few minutes.</p>
          ) : null}
        </div>
      )}
    </Card>
  );
}

// ----------------------------------------------------------------- wordpress
function WordPressCard({
  integration,
  siteId,
  canManage,
}: {
  integration?: IntegrationSummary;
  siteId: string;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ base_url: '', username: '', application_password: '' });
  const [open, setOpen] = useState(false);

  const mutation = useMutation({
    mutationFn: () => integrationApi.connectWordPress({ site_id: siteId, ...form }),
    onSuccess: async () => {
      setOpen(false);
      setForm({ base_url: '', username: '', application_password: '' });
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => integrationApi.disconnect(integration!.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['integrations'] }),
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    mutation.mutate();
  }

  return (
    <Card>
      <CardHeader
        title="WordPress"
        description="Apply approved changes directly through the WordPress REST API."
        action={<StatusRow integration={integration} />}
      />

      {integration && integration.status === 'connected' ? (
        <div className="space-y-3">
          <dl className="text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-ink-muted">Site</dt>
              <dd className="font-mono text-xs text-ink">
                {String(integration.config.base_url ?? '')}
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-ink-muted">SEO plugin detected</dt>
              <dd className="text-ink">
                {String(integration.capabilities.seo_plugin ?? 'none')}
              </dd>
            </div>
          </dl>
          {!integration.capabilities.seo_plugin ? (
            <Callout tone="warning">
              No supported SEO plugin was detected, so meta descriptions cannot be written. Titles
              and content changes still work.
            </Callout>
          ) : null}
          <Button
            size="sm"
            variant="ghost"
            disabled={!canManage}
            loading={disconnectMutation.isPending}
            onClick={() => disconnectMutation.mutate()}
          >
            Disconnect
          </Button>
        </div>
      ) : open ? (
        <form onSubmit={submit} className="space-y-4">
          {mutation.isError ? (
            <Callout tone="danger">
              {mutation.error instanceof ApiError ? mutation.error.message : 'Connection failed.'}
            </Callout>
          ) : null}
          {integration?.last_error ? <Callout tone="danger">{integration.last_error}</Callout> : null}

          <Field label="WordPress URL" htmlFor="wp-url" required>
            <input
              id="wp-url"
              required
              value={form.base_url}
              onChange={(event) => setForm({ ...form, base_url: event.target.value })}
              className={inputClass}
              placeholder="https://www.example.com"
            />
          </Field>
          <Field label="Username" htmlFor="wp-user" required>
            <input
              id="wp-user"
              required
              value={form.username}
              onChange={(event) => setForm({ ...form, username: event.target.value })}
              className={inputClass}
            />
          </Field>
          <Field
            label="Application password"
            htmlFor="wp-password"
            hint="Create one under Users → Profile → Application Passwords. Never use your login password."
            required
          >
            <input
              id="wp-password"
              type="password"
              required
              value={form.application_password}
              onChange={(event) => setForm({ ...form, application_password: event.target.value })}
              className={inputClass}
              placeholder="xxxx xxxx xxxx xxxx"
            />
          </Field>
          <div className="flex gap-2">
            <Button type="submit" variant="primary" loading={mutation.isPending}>
              Connect
            </Button>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <Button disabled={!canManage || !siteId} onClick={() => setOpen(true)}>
          Connect WordPress
        </Button>
      )}
    </Card>
  );
}

// ----------------------------------------------------------------------- git
function GitCard({
  integration,
  siteId,
  canManage,
}: {
  integration?: IntegrationSummary;
  siteId: string;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    repository: '',
    access_token: '',
    base_branch: 'main',
    content_root: '',
  });
  const [open, setOpen] = useState(false);

  const mutation = useMutation({
    mutationFn: () => integrationApi.connectGit({ site_id: siteId, ...form }),
    onSuccess: async () => {
      setOpen(false);
      setForm({ repository: '', access_token: '', base_branch: 'main', content_root: '' });
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => integrationApi.disconnect(integration!.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['integrations'] }),
  });

  return (
    <Card>
      <CardHeader
        title="GitHub"
        description="Approved changes open a pull request. Nothing is ever pushed to your production branch."
        action={<StatusRow integration={integration} />}
      />

      {integration && integration.status === 'connected' ? (
        <div className="space-y-3">
          <dl className="text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-ink-muted">Repository</dt>
              <dd className="font-mono text-xs text-ink">
                {String(integration.config.repository ?? '')}
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-ink-muted">Base branch</dt>
              <dd className="font-mono text-xs text-ink">
                {String(integration.config.base_branch ?? 'main')}
              </dd>
            </div>
          </dl>
          <Button
            size="sm"
            variant="ghost"
            disabled={!canManage}
            loading={disconnectMutation.isPending}
            onClick={() => disconnectMutation.mutate()}
          >
            Disconnect
          </Button>
        </div>
      ) : open ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            mutation.mutate();
          }}
          className="space-y-4"
        >
          {mutation.isError ? (
            <Callout tone="danger">
              {mutation.error instanceof ApiError ? mutation.error.message : 'Connection failed.'}
            </Callout>
          ) : null}

          <Field label="Repository" htmlFor="git-repo" hint="owner/name" required>
            <input
              id="git-repo"
              required
              pattern="[\w.-]+/[\w.-]+"
              value={form.repository}
              onChange={(event) => setForm({ ...form, repository: event.target.value })}
              className={inputClass}
              placeholder="acme/website"
            />
          </Field>
          <Field
            label="Personal access token"
            htmlFor="git-token"
            hint="Needs Contents: read & write and Pull requests: read & write on this repository only."
            required
          >
            <input
              id="git-token"
              type="password"
              required
              value={form.access_token}
              onChange={(event) => setForm({ ...form, access_token: event.target.value })}
              className={inputClass}
              placeholder="github_pat_…"
            />
          </Field>
          <Field label="Base branch" htmlFor="git-branch">
            <input
              id="git-branch"
              value={form.base_branch}
              onChange={(event) => setForm({ ...form, base_branch: event.target.value })}
              className={inputClass}
            />
          </Field>
          <Field
            label="Content directory"
            htmlFor="git-root"
            hint="Optional. Where page source files live, e.g. src/pages or content."
          >
            <input
              id="git-root"
              value={form.content_root}
              onChange={(event) => setForm({ ...form, content_root: event.target.value })}
              className={inputClass}
              placeholder="src/pages"
            />
          </Field>
          <div className="flex gap-2">
            <Button type="submit" variant="primary" loading={mutation.isPending}>
              Connect
            </Button>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <Button disabled={!canManage || !siteId} onClick={() => setOpen(true)}>
          Connect GitHub
        </Button>
      )}
    </Card>
  );
}
